import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";
import type { ISpeedControlParameterDto } from "@framework/types";

import { ANALYTICS_MEASURE_TYPE, type IChartData } from "../../scheme/components/analytics/interfaces";
import type { IStructureTreePoint, MULTI_ANALYSIS_MEASURE_TYPE } from "../types";
import type { IAnalysisTemplateScalarMeasure } from "../utils/analysis-template-settings";
import { mapMultiAnalysisMeasureTypeToAnalytics } from "../utils/measure-type-map";
import {
  fetchMultiAnalysisMeasureData,
  fetchMultiAnalysisMeasureOptions,
  fetchMultiAnalysisTrendMeasureData,
} from "../utils/measure-options";
import { normalizeMultiAnalysisTrendSeriesDates } from "../utils/range-frame";
import {
  buildParameterTrendSeries,
  buildScalarTrendSeries,
  filterParametersByUnitCodes,
  scalarMeasureMatchesOption,
  type IParameterTrendRow,
} from "../utils/template-scalar-measures";
import {
  buildMultiAnalysisTrendSeries,
  filterMeasureResponseByCellFilters,
  resolveMultiAnalysisTrendMeasureOptions,
  normalizeTrendRangeForRequest,
  type IMultiAnalysisRequestTimeRange,
  type IMultiAnalysisTimeRange,
} from "../utils/trend-series";

interface IUseMultiAnalysisTrendSeriesArgs {
  enabled: boolean;
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisTimeRange;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE | null;
  cellFilters?: Record<string, unknown>;
}

interface ILoadTrendSeriesArgs {
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisRequestTimeRange;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE;
  cellFilters: Record<string, unknown>;
  dbTz: number;
}

export const useMultiAnalysisTrendSeries = ({
  enabled,
  points,
  range,
  measureType,
  cellFilters = {},
}: IUseMultiAnalysisTrendSeriesArgs): { series: IChartData[]; isLoading: boolean } => {
  const { dbTz } = useAsu();
  const { formatMessage } = useIntl();
  const [series, setSeries] = useState<IChartData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const { fn: loadTrendSeries } = useApiCall(
    async (api, args: ILoadTrendSeriesArgs): Promise<IChartData[]> => {
      const pointIds = args.points.map(({ id }) => id);
      const analyticsMeasureType = mapMultiAnalysisMeasureTypeToAnalytics(args.measureType);

      const measureOptions = resolveMultiAnalysisTrendMeasureOptions(
        await fetchMultiAnalysisMeasureOptions(api, analyticsMeasureType, pointIds),
        args.measureType,
        args.cellFilters,
      );
      const measureNames = Array.from(new Set(measureOptions.map(({ title }) => title)));

      if (!measureNames.length) {
        return [];
      }

      const response = filterMeasureResponseByCellFilters(
        await fetchMultiAnalysisTrendMeasureData(api, {
          multiAnalysisMeasureType: args.measureType,
          pointIds,
          measureNames,
          range: args.range,
        }),
        args.cellFilters,
      );

      return normalizeMultiAnalysisTrendSeriesDates(
        buildMultiAnalysisTrendSeries(response, args.points, measureOptions, {
          unitLabelFormatter: unitCode => formatMessage({ id: `enums.units.${unitCode}` }),
          pointDirectionLabelFormatter: direction => formatMessage({ id: `enums.pointDirection.${direction}` }),
        }),
        args.dbTz,
      );
    },
    { success: false, error: false },
  );
  const loadTrendSeriesRef = useRef(loadTrendSeries);

  useEffect(() => {
    loadTrendSeriesRef.current = loadTrendSeries;
  }, [loadTrendSeries]);

  useEffect(() => {
    const requestRange = normalizeTrendRangeForRequest(range, dbTz);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || !points.length || !requestRange || !measureType) {
      setSeries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void loadTrendSeriesRef
      .current(undefined, { points, range: requestRange, measureType, cellFilters, dbTz })
      .then(nextSeries => {
        if (requestIdRef.current === requestId) {
          setSeries(nextSeries ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setSeries([]);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [cellFilters, dbTz, enabled, measureType, points, range]);

  return { series, isLoading };
};

interface IUseTemplateScalarTrendSeriesArgs {
  enabled: boolean;
  trainId: number | null;
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisTimeRange;
  scalarMeasures: readonly IAnalysisTemplateScalarMeasure[];
  parameterUnits: readonly string[];
}

interface ILoadScalarTrendSeriesArgs extends IUseTemplateScalarTrendSeriesArgs {
  dbTz: number;
  unitLabelFormatter: (unitCode: number) => string;
  kindLabelFormatter: (kind: ISpeedControlParameterDto["kind"]) => string;
  pointDirectionLabelFormatter: (direction: number) => string;
}

export const useTemplateScalarTrendSeries = ({
  enabled,
  trainId,
  points,
  range,
  scalarMeasures,
  parameterUnits,
}: IUseTemplateScalarTrendSeriesArgs): { series: IChartData[]; isLoading: boolean } => {
  const { dbTz } = useAsu();
  const { formatMessage } = useIntl();
  const [series, setSeries] = useState<IChartData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const scalarMeasuresKey = JSON.stringify(scalarMeasures);
  const parameterUnitsKey = JSON.stringify(parameterUnits);

  const { fn: loadSeries } = useApiCall(
    async (api, args: ILoadScalarTrendSeriesArgs): Promise<IChartData[]> => {
      const requestRange = normalizeTrendRangeForRequest(args.range, args.dbTz);
      const pointIds = args.points.map(point => point.id);
      const scalarSeries =
        args.scalarMeasures.length && pointIds.length && requestRange
          ? await (async () => {
              const measureOptions = await fetchMultiAnalysisMeasureOptions(
                api,
                ANALYTICS_MEASURE_TYPE.TREND,
                pointIds,
              );
              const matchingOptions = measureOptions.filter(option =>
                args.scalarMeasures.some(measure => scalarMeasureMatchesOption(measure, option)),
              );
              const measureNames = Array.from(new Set(matchingOptions.map(option => option.title))).filter(Boolean);

              if (!measureNames.length) {
                return [];
              }

              const response = await fetchMultiAnalysisMeasureData(api, {
                measureType: ANALYTICS_MEASURE_TYPE.TREND,
                pointIds,
                measureNames,
                range: requestRange,
              });

              return buildScalarTrendSeries(response, args.points, matchingOptions, args.scalarMeasures, {
                unitLabelFormatter: args.unitLabelFormatter,
                pointDirectionLabelFormatter: args.pointDirectionLabelFormatter,
              });
            })().catch(error => {
              console.error(error);
              return [] as IChartData[];
            })
          : [];
      const parameterSeries =
        args.parameterUnits.length && args.trainId !== null && requestRange
          ? await (async () => {
              const parameters = (await api.fetchJson({
                url: `/trains/${args.trainId}/speed-control-parameters`,
                method: "GET",
              })) as ISpeedControlParameterDto[] | null | undefined;
              const matchingParameters = filterParametersByUnitCodes(parameters ?? [], args.parameterUnits);
              const rowsByDataId = new Map<number, IParameterTrendRow[]>();

              await Promise.all(
                matchingParameters.map(async parameter => {
                  try {
                    const rows = (await api.fetchJson({
                      url: `/trains/${args.trainId}/speed-control-parameter-data`,
                      method: "GET",
                      data: {
                        idxKey: parameter.dataId,
                        startTimestamp: requestRange.startTimestamp,
                        endTimestamp: requestRange.endTimestamp,
                      },
                    })) as IParameterTrendRow[] | null | undefined;

                    rowsByDataId.set(parameter.dataId, rows ?? []);
                  } catch (error) {
                    console.error(error);
                    rowsByDataId.set(parameter.dataId, []);
                  }
                }),
              );

              return buildParameterTrendSeries(matchingParameters, rowsByDataId, {
                trainId: args.trainId ?? 0,
                unitLabelFormatter: args.unitLabelFormatter,
                kindLabelFormatter: args.kindLabelFormatter,
                colorOffset: scalarSeries.length,
              });
            })().catch(error => {
              console.error(error);
              return [] as IChartData[];
            })
          : [];

      return normalizeMultiAnalysisTrendSeriesDates([...scalarSeries, ...parameterSeries], args.dbTz);
    },
    { success: false, error: false },
  );
  const loadSeriesRef = useRef(loadSeries);

  useEffect(() => {
    loadSeriesRef.current = loadSeries;
  }, [loadSeries]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const parsedScalarMeasures = JSON.parse(scalarMeasuresKey) as IAnalysisTemplateScalarMeasure[];
    const parsedParameterUnits = JSON.parse(parameterUnitsKey) as string[];

    if (!enabled || (!parsedScalarMeasures.length && !parsedParameterUnits.length)) {
      setSeries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void loadSeriesRef
      .current(undefined, {
        trainId,
        points,
        range,
        scalarMeasures: parsedScalarMeasures,
        parameterUnits: parsedParameterUnits,
        dbTz,
        unitLabelFormatter: (unitCode: number) => formatMessage({ id: `enums.units.${unitCode}` }),
        kindLabelFormatter: (kind: ISpeedControlParameterDto["kind"]) =>
          formatMessage({ id: `enums.idParameter.${kind}` }),
        pointDirectionLabelFormatter: (direction: number) => formatMessage({ id: `enums.pointDirection.${direction}` }),
      })
      .then(nextSeries => {
        if (requestIdRef.current === requestId) {
          setSeries(nextSeries ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setSeries([]);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [dbTz, enabled, formatMessage, parameterUnitsKey, points, range, scalarMeasuresKey, trainId]);

  return { series, isLoading };
};
